import React from "react";
import { DotLegalAlert, DotLegalButton, DotLegalFullScreenSpinner, DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import DotLegalStepper from "@common/components/dotLegalStepper/DotLegalStepper";
import { useTranslation } from "@localization/useTranslation";
import { LegalEntityScope } from "../legalEntities/LegalEntities.types";
import { useLegalEntityBreadCrumbs } from "../useLegalEntityBreadCrumbs";
import { useEditLegalEntity } from "./EditLegalEntity.hooks";
import LegalEntityStep from "./steps/LegalEntityStep";

export interface IEditLegalEntity {
    legalEntityScope: LegalEntityScope;
}

function EditLegalEntity(props: IEditLegalEntity) {
    const { translateString } = useTranslation();

    const {
        isLoading,
        legalEntity,
        legalEntityTemplate,
        steps,
        activeStep,
        changeStep,
        onChange,
        onCachedChange,
        validationErrors,
        legalEntityId,
        refetchLegalEntity,
        onComplete,
        returnBannerTranslationKey,
        returnName,
        abortReturnText,
        onAbortReturn,
    } = useEditLegalEntity(props);

    const breadCrumbs = useLegalEntityBreadCrumbs(props.legalEntityScope, {
        name: legalEntity?.name ?? "",
        id: legalEntity?.id ?? "",
        userFriendlyId: legalEntity?.userFriendlyId,
        description: legalEntity?.description ?? "",
    });

    return (
        <React.Fragment>
            <DotLegalPageHeader breadCrumbs={breadCrumbs} />
            {returnBannerTranslationKey && (
                <DotLegalAlert
                    severity={"info"}
                    warningText={<Trans i18nKey={returnBannerTranslationKey} values={{ name: returnName }} />}
                    action={
                        onAbortReturn && (
                            <DotLegalButton buttonType="linkButton" onClick={onAbortReturn}>
                                {abortReturnText}
                            </DotLegalButton>
                        )
                    }
                />
            )}
            {isLoading ? (
                <DotLegalFullScreenSpinner />
            ) : (
                <DotLegalStepper
                    steps={steps}
                    activeStep={activeStep.stepType}
                    header={activeStep.name}
                    onStepClick={(step) => changeStep(step)}
                    onSaveClick={onComplete}
                    overviewButtonText={translateString("complete")}
                >
                    <LegalEntityStep
                        isLoading={isLoading}
                        legalEntityId={legalEntityId!}
                        legalEntity={legalEntity}
                        legalEntityTemplateStep={legalEntityTemplate?.steps.find((x) => x.order === activeStep.stepType)!}
                        onChange={onChange}
                        onCachedChange={onCachedChange}
                        onRefetchLegalEntity={refetchLegalEntity}
                        validationErrors={validationErrors}
                    />
                </DotLegalStepper>
            )}
        </React.Fragment>
    );
}

export default EditLegalEntity;
